/*
 * Copyright 2020 Thomer Gil [https://thomer.com/games/brickbreaker-js/]
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
// {{{ imports
import * as ex from 'excalibur';

import { Wall } from './wall';
import { GameScene } from './gamescene';
import { PauseScene } from './pausescene';
import { Ball } from './ball';
import { Paddle } from './paddle';
import { BB } from './globals';
// }}}

export class Game extends ex.Engine {
    // {{{ members, constructors
    private _balls: Array<Ball>;
    private _ballsLost: number;
    private _ballsPlayed: number;
    private _gameScene: GameScene;
    private _level: number;
    private _levelLabel: ex.Label;
    private _lives: Array<ex.Actor>;
    private _livesLeft: number;
    private _paddle: Paddle;
    private _pauseScene: PauseScene;
    private _vMult: number;
    private _wall: Wall;
    private _countdownLabel: ex.Label;
    private _gameOverLabel: ex.Label;
    private static _instance: Game;

    // Singleton method
    public static Instance(): Game {
        if (!Game._instance) {
            Game._instance = new Game();
        }
        return Game._instance;
    }

    private constructor() {
        super({
            width: BB.screenWidth,
            height: BB.screenHeight,
            backgroundColor: BB.BGCOLOR,
            suppressConsoleBootMessage: true,
            pointerScope: ex.PointerScope.Document,
            displayMode: ex.DisplayMode.Fixed,
        });

        this._balls = new Array<Ball>();
        this._ballsLost = 0;
        this._ballsPlayed = 0;
        this._gameScene = null;
        this._level = BB.LEVEL_START;
        this._levelLabel = null;
        this._lives = new Array<ex.Actor>();
        this._livesLeft = BB.NLIVES - 1;
        this._paddle = null;
        this._pauseScene = null;
        this._vMult = BB.LEVEL_UP_MULT ** (BB.LEVEL_START - 1);
        this._wall = null;
        this.timescale = BB.TIMESCALE;

        // Initialization will be called from start() override

        // level up when wall is empty
        this.on('postupdate', () => {
            if (this._wall && this._wall.isEmpty()) {
                this._wall = null; // prevent multiple calls
                this.levelup();
            }
        });
    }
    // }}}

    // {{{ public
    // cleans up a ball that fell below the paddle
    public loseBall(ball: Ball): void {
        // because we ponder the death of a ball for a while (by means of
        // this.sleep()), we need to ensure we don't re-enter this function. to
        // achieve that: look up the ball that's being lost and remove it. if
        // it's already been removed, then this is a re-entrant call: return
        // out of it.
        const b = this._balls.findIndex((b) => b === ball);
        if (b === -1) {
            return;
        }
        this._balls.splice(b, 1);
        this._ballsLost += 1;

        // Remove ball from scene immediately
        this._gameScene.remove(ball);

        // the last ball is gone;
        if (this._balls.length <= 0) {
            this.sleep(BB.DEATH_SLEEP).then(() => {

                // only count lives if not running in infinite mode
                if (!BB.LIVES_INFINITE) {
                    this._livesLeft -= 1;
                }
                this._redrawLives();

                // we still have lives left
                if (this._livesLeft >= 0) {
                    this._paddle.reset();
                    this.removeBalls();
                    this._spawnBalls();
                } else {
                    // go back to level 1
                    this.showGameOver('Game Over!').then(() => {
                        this._level = 0;
                        this._livesLeft = BB.NLIVES - 2; // levelup() will add one
                        this.levelup();
                    });
                }
            });
        }
    }

    //
    // ends the current level, removes scenes, and creates the next one
    //
    public levelup(): void {
        this._level += 1;
        if (this._level > BB.BRICK_COLORS.length) {
            this.showGameOver('You are Brick Breaker God!').then(() => {
                this._level = 0;
                this._livesLeft = BB.NLIVES - 2;
                this.levelup();
            });
            return;
        }

        this.sleep(BB.DEATH_SLEEP).then(() => {
            this._livesLeft += 1;
            this._vMult = BB.LEVEL_UP_MULT ** (this._level - 1);
            this._createScenesAndActors();
            this.play();
        });
    }

    //
    // pauses the game for the given number of seconds (XXX?)
    //
    // achieve this by flipping to the pauseScene and reminding it to flip
    // back. be aware that any code that runs after this sleep() call will
    // still execute. one particularly relevant example is loseBall:
    //
    public sleep(delay: number): Promise<void> {
        // Optimize the delay == 0 case
        if (!delay) {
            return new Promise((resolve) => {
                resolve();
            });
        }

        this._gameScene.pause();
        return new Promise((resolve) => {
            // Use engine clock.schedule - works regardless of active scene
            this.clock.schedule(() => {
                this._pauseScene.unpause();
                resolve();
            }, delay * 1000);
        });
    }

    // Shows countdown (3, 2, 1, GO!) and resolves when done
    public countdown(): Promise<void> {
        const INTERVAL = 350; // ms per message
        return new Promise((resolve) => {
            const messages = ['3', '2', '1', 'GO!'];
            let index = 0;

            // Create countdown label if it doesn't exist
            if (!this._countdownLabel) {
                this._countdownLabel = new ex.Label({
                    pos: new ex.Vector(this.halfDrawWidth, this.halfDrawHeight),
                    font: new ex.Font({
                        family: 'Arial',
                        size: 48,
                        unit: ex.FontUnit.Px,
                        color: BB.FGCOLOR,
                        textAlign: ex.TextAlign.Center,
                    }),
                });
            }

            // Add to scene and show first message
            this._countdownLabel.text = messages[index];
            this._gameScene.add(this._countdownLabel);

            // Use clock.schedule for countdown steps
            const showNext = () => {
                index++;
                if (index < messages.length) {
                    this._countdownLabel.text = messages[index];
                    this.clock.schedule(showNext, INTERVAL);
                } else {
                    // Countdown finished - remove label and resolve
                    this._gameScene.remove(this._countdownLabel);
                    resolve();
                }
            };

            // Start countdown sequence
            this.clock.schedule(showNext, INTERVAL);
        });
    }

    // Shows game over message and waits for 'p' to restart
    public showGameOver(message: string): Promise<void> {
        return new Promise((resolve) => {
            // Create game over label if it doesn't exist
            if (!this._gameOverLabel) {
                this._gameOverLabel = new ex.Label({
                    pos: new ex.Vector(this.halfDrawWidth, this.halfDrawHeight),
                    font: new ex.Font({
                        family: 'Arial',
                        size: 32,
                        unit: ex.FontUnit.Px,
                        color: BB.FGCOLOR,
                        textAlign: ex.TextAlign.Center,
                    }),
                });
            }

            // Show message with instruction
            this._gameOverLabel.text = message + "\nPress 'p' to play again";
            this._gameScene.add(this._gameOverLabel);

            // Listen for 'p' key (character, not keycode - for keyboard layout support)
            const keyHandler = (e: KeyboardEvent) => {
                if (e.key.toLowerCase() === 'p' && !e.repeat) {
                    window.removeEventListener('keydown', keyHandler);
                    this._gameScene.remove(this._gameOverLabel);
                    resolve();
                }
            };
            window.addEventListener('keydown', keyHandler);
        });
    }

    // which ball to follow for auto mode
    public autoFollowBall(): Ball {
        return this._balls[0];
    }

    public pause(): void {
        this.goToScene('pause');
    }

    public onInitialize(): void {
        this._createScenesAndActors();
        this.play();
    }

    public play(): void {
        this.goToScene('play');
    }
    // }}}

    // {{{ private
    //
    // creates wall, balls, paddle, and information at the top
    //
    private _createScenesAndActors(): void {
        // In Excalibur 0.30, cannot remove an active scene
        // Only remove scenes that aren't currently active
        const currentSceneName = this.currentScene?.constructor.name;

        // pauseScene
        if (this._pauseScene && currentSceneName !== 'PauseScene') {
            this.removeScene('pause');
            this._pauseScene = null;
        }
        if (!this._pauseScene) {
            this._pauseScene = new PauseScene();
            this.addScene('pause', this._pauseScene);
        }

        // gameScene
        if (this._gameScene && currentSceneName !== 'GameScene') {
            this.removeScene('play');
            this._gameScene = null;
        }
        if (!this._gameScene) {
            this._gameScene = new GameScene(this._pauseScene);
            this.addScene('play', this._gameScene);
        }

        // bricks
        this._wall = new Wall(this._gameScene, this._level);

        // paddle
        if (!this._paddle) {
            this._paddle = new Paddle(this._vMult);
            this._paddle.on('collisionstart', () => {
                if (this._ballsPlayed + this._ballsLost < BB.NBALLS) {
                    this._spawnBalls();
                }
            });
        }
        this._gameScene.add(this._paddle);
        this._paddle.reset();
        this._paddle.setVMult(this._vMult);

        // info string on top
        this._redrawLives();

        // balls; need to come last
        this.removeBalls();
        this._spawnBalls();
    }

    // redraw the lives as balls that don't interact with the rest of the game
    private _redrawLives(): void {
        this._lives.forEach((life) => {
            this._gameScene.remove(life);
            life.kill();
        });
        this._lives.splice(0, this._lives.length); // empty array
        for (let i = 0; i < this._livesLeft; i++) {
            const life = new ex.Actor({
                pos: new ex.Vector(BB.LIVES_X + i * BB.LIVES_SPACE, BB.LIVES_Y),
                color: BB.BALL_COLOR,
                radius: BB.LIVES_BALL_RADIUS,
                collisionType: ex.CollisionType.PreventCollision,
            });
            this._lives.push(life);
            this._gameScene.add(life);
        }

        if (this._levelLabel) {
            this._gameScene.remove(this._levelLabel);
            this._levelLabel.kill();
        }
        this._levelLabel = new ex.Label({
            text: 'L: ' + this._level,
            pos: new ex.Vector(this.drawWidth - BB.LEVEL_X_FROM_RIGHT, BB.LEVEL_Y),
            font: new ex.Font({
                family: 'Arial',
                size: BB.LEVEL_SIZE,
                unit: ex.FontUnit.Px,
                color: BB.FGCOLOR,
                textAlign: ex.TextAlign.Right,
            }),
        });
        this._gameScene.add(this._levelLabel);
    }

    // spawns a new ball into being
    private _spawnBalls(): void {
        // offset the ball left/right by a BALL_RELEASE_X_JITTER %
        // in short: x + (random number between 0-4%) - 2%.
        // jitter increases with each level.
        const xJitter =
            Math.random() * (((BB.BALL_RELEASE_X_JITTER + this._level) / 100) * 2 * this.drawWidth) -
            ((BB.BALL_RELEASE_X_JITTER + this._level) / 100) * this.drawWidth;
        const ballInitXY = new ex.Vector(
            this.halfDrawWidth + xJitter,
            this._wall.bottomY() + BB.BELOW_WALL_SPACE_TO_BALL,
        );

        const ball = new Ball(ballInitXY, this._vMult);
        this._balls.push(ball);
        this._gameScene.add(ball);
        this._ballsPlayed += 1;

        // Hide ball during countdown, then show and release
        ball.graphics.visible = false;
        this.countdown().then(() => {
            ball.graphics.visible = true;
            ball.release();
        });
    }

    //
    // removes all balls from the current gameScene
    //
    private removeBalls(): void {
        this._ballsLost = 0;
        this._ballsPlayed = 0;
        this._balls.forEach((ball) => {
            this._gameScene.remove(ball);
            ball.kill();
        });
        this._balls.splice(0, this._balls.length); // empty array
    }
    // }}}
}
